import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { 
  Plus, 
  Apple,
  Flame,
  Sparkles,
  Trash2,
  Coffee,
  Sun,
  Moon as MoonIcon,
  Cookie
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const NutritionPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewDialog, setShowNewDialog] = useState(searchParams.get('action') === 'new');
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [newLog, setNewLog] = useState({
    meal_type: 'breakfast',
    foods: [{ name: '', calories: 0, protein: 0, carbs: 0, fat: 0 }],
    date: new Date().toLocaleDateString('en-CA'), // Local date YYYY-MM-DD
    notes: ''
  });

  const [dailyTotals, setDailyTotals] = useState({
    calories: 0,
    protein: 0,
    carbs: 0,
    fat: 0
  });

  const targets = {
    calories: 2500,
    protein: 150,
    carbs: 300,
    fat: 80
  };

  useEffect(() => {
    fetchNutritionLogs();
  }, []);

  useEffect(() => {
    // Calculate daily totals using local date
    const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD in local time
    const todayLogs = logs.filter(log => log.date === today);
    const totals = todayLogs.reduce((acc, log) => ({
      calories: acc.calories + (log.total_calories || 0),
      protein: acc.protein + (log.total_protein || 0),
      carbs: acc.carbs + (log.total_carbs || 0),
      fat: acc.fat + (log.total_fat || 0)
    }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
    setDailyTotals(totals);
  }, [logs]);

  const fetchNutritionLogs = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/nutrition`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch nutrition logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLog = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/nutrition`, newLog, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success('Fuel complete. Performance supported.');
      setShowNewDialog(false);
      setNewLog({
        meal_type: 'breakfast',
        foods: [{ name: '', calories: 0, protein: 0, carbs: 0, fat: 0 }],
        date: new Date().toLocaleDateString('en-CA'), // Local date
        notes: ''
      });
      fetchNutritionLogs();
    } catch (error) {
      toast.error('Failed to log meal');
    }
  };

  const handleDeleteLog = async (logId) => {
    try {
      const token = localStorage.getItem('aura_token');
      await axios.delete(`${API}/nutrition/${logId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success('Meal deleted');
      fetchNutritionLogs();
    } catch (error) {
      toast.error('Failed to delete meal');
    }
  };

  const handleGetAISuggestion = async () => {
    setAiLoading(true);
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.post(`${API}/ai/nutrition-suggestion`, {
        context: 'nutrition',
        sport: user?.sport,
        user_goals: 'optimize performance nutrition'
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setAiSuggestion(response.data.suggestion);
    } catch (error) {
      toast.error('Failed to get AI suggestion');
    } finally {
      setAiLoading(false);
    }
  };

  const addFoodItem = () => {
    setNewLog({
      ...newLog,
      foods: [...newLog.foods, { name: '', calories: 0, protein: 0, carbs: 0, fat: 0 }]
    });
  };

  const updateFoodItem = (index, field, value) => {
    const updatedFoods = [...newLog.foods];
    updatedFoods[index][field] = field === 'name' ? value : parseFloat(value) || 0;
    setNewLog({ ...newLog, foods: updatedFoods });
  };

  const removeFoodItem = (index) => {
    const updatedFoods = newLog.foods.filter((_, i) => i !== index);
    setNewLog({ ...newLog, foods: updatedFoods });
  };

  // Quick-add common foods with pre-filled macros
  const quickAddFoods = [
    { name: 'Chicken Breast (6oz)', calories: 280, protein: 53, carbs: 0, fat: 6 },
    { name: 'Brown Rice (1 cup)', calories: 215, protein: 5, carbs: 45, fat: 2 },
    { name: 'Banana', calories: 105, protein: 1, carbs: 27, fat: 0 },
    { name: 'Greek Yogurt (1 cup)', calories: 150, protein: 20, carbs: 9, fat: 4 },
    { name: 'Protein Shake', calories: 200, protein: 30, carbs: 10, fat: 3 },
    { name: 'Eggs (2 large)', calories: 140, protein: 12, carbs: 1, fat: 10 },
    { name: 'Oatmeal (1 cup)', calories: 150, protein: 5, carbs: 27, fat: 3 },
    { name: 'Sweet Potato (medium)', calories: 103, protein: 2, carbs: 24, fat: 0 },
    { name: 'Salmon (4oz)', calories: 230, protein: 25, carbs: 0, fat: 14 },
    { name: 'Broccoli (1 cup)', calories: 55, protein: 4, carbs: 11, fat: 0 },
    { name: 'Almonds (1oz)', calories: 165, protein: 6, carbs: 6, fat: 14 },
    { name: 'Whole Wheat Bread (2 slices)', calories: 160, protein: 8, carbs: 30, fat: 2 },
  ];

  const addQuickFood = (food) => {
    setNewLog({
      ...newLog,
      foods: [...newLog.foods.filter(f => f.name !== ''), food]
    });
    toast.success(`Added ${food.name}`);
  };

  const getMealIcon = (type) => {
    switch (type) {
      case 'breakfast': return Coffee;
      case 'lunch': return Sun;
      case 'dinner': return MoonIcon;
      case 'snack': return Cookie;
      default: return Apple;
    }
  };

  // Sample 14-day meal rotation (from your docs)
  const mealRotation = [
    { day: 1, breakfast: 'Smoothie + Toast', lunch: 'Turkey Wrap + Veggies', dinner: 'Grilled Chicken + Rice', snack: 'Trail Mix' },
    { day: 2, breakfast: 'Greek Yogurt + Fruit', lunch: 'Taco Bowl', dinner: 'Salmon + Quinoa', snack: 'Apple + PB' },
    { day: 3, breakfast: 'Eggs + Whole Grain Toast', lunch: 'Chicken Salad', dinner: 'Stir-Fry', snack: 'Protein Bar' },
  ];

  return (
    <DashboardLayout title="Nutrition System">
      <div className="px-4 py-6 max-w-7xl mx-auto space-y-6">
        {/* Section Header */}
        <div className="mb-2">
          <p className="text-aura-text-muted text-sm">Fuel your body for performance.</p>
        </div>
        
        {/* Daily Macros Overview */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider">
              Today's Performance Fuel
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-aura-text-muted uppercase">Calories</span>
                  <span className="text-xs text-aura-text-muted">{dailyTotals.calories}/{targets.calories}</span>
                </div>
                <Progress value={(dailyTotals.calories / targets.calories) * 100} className="h-2 bg-white/10" />
                <p className="font-heading font-bold text-xl text-aura-primary mt-1">{dailyTotals.calories}</p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-aura-text-muted uppercase">Protein</span>
                  <span className="text-xs text-aura-text-muted">{dailyTotals.protein}g/{targets.protein}g</span>
                </div>
                <Progress value={(dailyTotals.protein / targets.protein) * 100} className="h-2 bg-white/10" />
                <p className="font-heading font-bold text-xl text-aura-success mt-1">{dailyTotals.protein}g</p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-aura-text-muted uppercase">Carbs</span>
                  <span className="text-xs text-aura-text-muted">{dailyTotals.carbs}g/{targets.carbs}g</span>
                </div>
                <Progress value={(dailyTotals.carbs / targets.carbs) * 100} className="h-2 bg-white/10" />
                <p className="font-heading font-bold text-xl text-aura-secondary mt-1">{dailyTotals.carbs}g</p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-aura-text-muted uppercase">Fat</span>
                  <span className="text-xs text-aura-text-muted">{dailyTotals.fat}g/{targets.fat}g</span>
                </div>
                <Progress value={(dailyTotals.fat / targets.fat) * 100} className="h-2 bg-white/10" />
                <p className="font-heading font-bold text-xl text-aura-warning mt-1">{dailyTotals.fat}g</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
            <DialogTrigger asChild>
              <Button className="flex-1 sm:flex-none bg-aura-success hover:bg-aura-success/90 text-white font-heading uppercase tracking-wider h-12" data-testid="log-meal-btn">
                <Plus className="w-5 h-5 mr-2" />
                Fuel Up
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-aura-surface border-white/10 max-w-md mx-4 max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading font-bold text-xl text-white uppercase tracking-wider">
                  Fuel Performance
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateLog} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Meal Type</Label>
                    <Select value={newLog.meal_type} onValueChange={(v) => setNewLog({ ...newLog, meal_type: v })}>
                      <SelectTrigger className="h-12 bg-aura-bg border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-aura-surface border-white/10">
                        <SelectItem value="breakfast" className="text-white">Breakfast</SelectItem>
                        <SelectItem value="lunch" className="text-white">Lunch</SelectItem>
                        <SelectItem value="dinner" className="text-white">Dinner</SelectItem>
                        <SelectItem value="snack" className="text-white">Snack</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Date</Label>
                    <Input
                      type="date"
                      value={newLog.date}
                      onChange={(e) => setNewLog({ ...newLog, date: e.target.value })}
                      className="h-12 bg-aura-bg border-white/10 text-white"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Quick Add</Label>
                    <p className="text-[10px] text-aura-text-muted mt-0.5">Tap to auto-fill nutrition values</p>
                  </div>
                  <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                    {quickAddFoods.slice(0, 8).map((food) => (
                      <button
                        key={food.name}
                        type="button"
                        onClick={() => addQuickFood(food)}
                        className="px-2 py-1 text-xs bg-white/5 hover:bg-aura-success/20 hover:border-aura-success/30 rounded border border-white/10 text-aura-text-muted hover:text-white transition-colors"
                        title={`${food.calories} cal • ${food.protein}g protein`}
                      >
                        {food.name.split(' (')[0]}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Foods</Label>
                    {newLog.foods.length === 1 && newLog.foods[0].name === '' && (
                      <span className="text-[10px] text-aura-text-muted">Use Quick Add above or enter manually</span>
                    )}
                  </div>
                  {newLog.foods.map((food, index) => (
                    <div key={index} className={`aura-card p-3 space-y-3 ${food.name && food.calories > 0 ? 'border-aura-success/30' : ''}`}>
                      <div className="flex items-center gap-2">
                        <Input
                          placeholder="Food name (or use Quick Add)"
                          value={food.name}
                          onChange={(e) => updateFoodItem(index, 'name', e.target.value)}
                          className="flex-1 h-10 bg-aura-bg border-white/10 text-white text-sm"
                        />
                        {newLog.foods.length > 1 && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => removeFoodItem(index)}
                            className="text-aura-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        <div>
                          <Label className="text-[10px] text-aura-text-subtle">Cal</Label>
                          <Input
                            type="number"
                            value={food.calories}
                            onChange={(e) => updateFoodItem(index, 'calories', e.target.value)}
                            className="h-9 bg-aura-bg border-white/10 text-white text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] text-aura-text-subtle">Protein</Label>
                          <Input
                            type="number"
                            value={food.protein}
                            onChange={(e) => updateFoodItem(index, 'protein', e.target.value)}
                            className="h-9 bg-aura-bg border-white/10 text-white text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] text-aura-text-subtle">Carbs</Label>
                          <Input
                            type="number"
                            value={food.carbs}
                            onChange={(e) => updateFoodItem(index, 'carbs', e.target.value)}
                            className="h-9 bg-aura-bg border-white/10 text-white text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] text-aura-text-subtle">Fat</Label>
                          <Input
                            type="number"
                            value={food.fat}
                            onChange={(e) => updateFoodItem(index, 'fat', e.target.value)}
                            className="h-9 bg-aura-bg border-white/10 text-white text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addFoodItem}
                    className="w-full border-dashed border-white/20 text-aura-text-muted"
                  >
                    + Add Food Item
                  </Button>
                </div>

                <Button type="submit" className="w-full bg-aura-success hover:bg-aura-success/90 text-white font-heading uppercase tracking-wider h-12" data-testid="save-meal-btn">
                  Complete Fuel
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                className="flex-1 sm:flex-none border-aura-warning/50 text-aura-warning hover:bg-aura-warning/10 font-heading uppercase tracking-wider h-12"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                AI Meal Suggestion
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-aura-surface border-white/10 max-w-lg mx-4 max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading font-bold text-xl text-white uppercase tracking-wider flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-aura-warning" />
                  AI Nutrition Plan
                </DialogTitle>
              </DialogHeader>
              <div className="mt-4">
                {!aiSuggestion && !aiLoading && (
                  <div className="text-center py-8">
                    <Sparkles className="w-12 h-12 text-aura-warning mx-auto mb-4" />
                    <p className="text-aura-text-muted mb-4">
                      Get personalized meal suggestions based on your goals and what you've eaten today
                    </p>
                    <Button 
                      onClick={handleGetAISuggestion}
                      className="bg-aura-warning hover:bg-aura-warning/90 text-black font-heading uppercase tracking-wider"
                    >
                      Get Suggestions
                    </Button>
                  </div>
                )}
                {aiLoading && (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 border-4 border-aura-warning border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-aura-text-muted">Analyzing your nutrition...</p>
                  </div>
                )}
                {aiSuggestion && (
                  <div className="space-y-4">
                    {aiSuggestion.analysis && (
                      <div className="aura-card p-4">
                        <h4 className="font-heading font-bold text-sm text-white uppercase mb-2">Analysis</h4>
                        <p className="text-sm text-aura-text-muted">{aiSuggestion.analysis}</p>
                      </div>
                    )}
                    
                    {aiSuggestion.meal_suggestions && (
                      <div className="space-y-3">
                        <h4 className="font-heading font-bold text-sm text-aura-text-muted uppercase">Suggested Meals</h4>
                        {aiSuggestion.meal_suggestions.map((meal, i) => (
                          <div key={i} className="aura-card p-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-white">{meal.name}</span>
                              <span className="text-sm text-aura-primary">{meal.macros?.calories} cal</span>
                            </div>
                            <p className="text-sm text-aura-text-muted">{meal.ingredients?.join(', ')}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {aiSuggestion.tips && (
                      <div className="aura-card p-4 bg-aura-success/10 border-aura-success/30">
                        <h4 className="font-heading font-bold text-sm text-white uppercase mb-2">Tips</h4>
                        <ul className="text-sm text-aura-text-muted space-y-1">
                          {aiSuggestion.tips.map((tip, i) => (
                            <li key={i}>• {tip}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Meal Rotation Preview */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider">
              14-Day Meal Rotation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto -mx-4 px-4">
              <table className="w-full min-w-[500px]">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-2 text-xs text-aura-text-muted uppercase tracking-wider">Day</th>
                    <th className="text-left py-2 text-xs text-aura-text-muted uppercase tracking-wider">Breakfast</th>
                    <th className="text-left py-2 text-xs text-aura-text-muted uppercase tracking-wider">Lunch</th>
                    <th className="text-left py-2 text-xs text-aura-text-muted uppercase tracking-wider">Dinner</th>
                    <th className="text-left py-2 text-xs text-aura-text-muted uppercase tracking-wider">Snack</th>
                  </tr>
                </thead>
                <tbody>
                  {mealRotation.map((row) => (
                    <tr key={row.day} className="border-b border-white/5">
                      <td className="py-3 font-heading font-bold text-white">{row.day}</td>
                      <td className="py-3 text-sm text-white">{row.breakfast}</td>
                      <td className="py-3 text-sm text-white">{row.lunch}</td>
                      <td className="py-3 text-sm text-white">{row.dinner}</td>
                      <td className="py-3 text-sm text-white">{row.snack}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Recent Meals */}
        <div>
          <h2 className="font-heading font-bold text-lg text-white uppercase tracking-wider mb-4">
            Recent Meals
          </h2>
          
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="aura-card p-4 animate-pulse">
                  <div className="h-16 bg-white/5 rounded-lg" />
                </div>
              ))}
            </div>
          ) : logs.length > 0 ? (
            <div className="space-y-3">
              {logs.slice(0, 10).map((log) => {
                const MealIcon = getMealIcon(log.meal_type);
                return (
                  <Card key={log.log_id} className="bg-aura-surface border-white/10">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className="w-10 h-10 rounded-lg bg-aura-success/20 flex items-center justify-center flex-shrink-0">
                            <MealIcon className="w-5 h-5 text-aura-success" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-white capitalize">{log.meal_type}</h3>
                            <p className="text-sm text-aura-text-muted truncate">
                              {log.foods.map(f => f.name).join(', ')}
                            </p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-aura-text-subtle">
                              <span>{log.total_calories} cal</span>
                              <span>{log.total_protein}g protein</span>
                              <span>{log.date}</span>
                            </div>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteLog(log.log_id)}
                          className="text-aura-text-muted hover:text-aura-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="bg-aura-surface border-white/10">
              <CardContent className="p-8 text-center">
                <Apple className="w-12 h-12 text-aura-text-subtle mx-auto mb-4" />
                <h3 className="font-heading font-bold text-lg text-white mb-2">Fuel Your Performance</h3>
                <p className="text-aura-text-muted mb-4">
                  Fuel your body to perform. Start here.
                </p>
                <Button 
                  onClick={() => setShowNewDialog(true)}
                  className="bg-aura-success hover:bg-aura-success/90 text-white"
                >
                  Log First Meal
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default NutritionPage;
